/**
 * Analytics periods, shared by every dashboard that compares a window against the one
 * before it.
 *
 * Every range carries its own predecessor, because «is this better than last month» is the
 * question these pages exist to answer, and the previous window is not always the naive
 * shift: `this_month` compares against the same day-range of the previous month, so the
 * 9th of March is compared with the 1st–9th of February rather than all of it.
 *
 * Timezone-aware throughout: a «day» is the customer's day, not UTC's.
 */

import { getTimeZoneFormattedParts, toDateKeyTz, zonedTimeToUtc } from "@/lib/date-utils"

export type AnalyticsPeriod = "today" | "this_month" | "7d" | "30d" | "90d" | "all" | "custom"

export const ANALYTICS_PERIODS: AnalyticsPeriod[] = [
  "today",
  "this_month",
  "7d",
  "30d",
  "90d",
  "all",
  "custom",
]

/** Performance guardrail on «all time». */
export const MAX_HISTORY_YEARS = 1

export const PERIOD_OPTIONS: { value: AnalyticsPeriod; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "this_month", label: "This Month" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: `Last ${MAX_HISTORY_YEARS} year` },
  { value: "custom", label: "Custom Range" },
]

export interface AnalyticsDateRange {
  start: Date;
  end: Date;
  prevStart: Date;
  /** End of the previous window. Defaults to `start` when unset (prevStart → start). */
  prevEnd?: Date;
}

export interface PeriodToDateRangeOptions {
  /** `YYYY-MM-DD` bounds, read only for the `custom` period. */
  customRange?: { from?: string | null; to?: string | null };
  /** Oldest row the caller holds, so `all` does not span emptier history than exists. */
  oldestDate?: Date | null;
}

export function isValidAnalyticsPeriod(period: string): period is AnalyticsPeriod {
  return (ANALYTICS_PERIODS as string[]).includes(period)
}

export function validateTimezone(tz: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch {
    return false
  }
}

const PRESET_DAYS: Record<string, number> = { "7d": 7, "30d": 30, "90d": 90 }

export function periodToDateRange(
  period: AnalyticsPeriod,
  tz: string,
  options: PeriodToDateRangeOptions = {}
): AnalyticsDateRange {
  const now = new Date()
  const end = now
  const todayStr = toDateKeyTz(now, tz)

  if (period === "today") {
    const start = zonedTimeToUtc(`${todayStr}T00:00:00`, tz)
    const yesterdayDate = new Date(start.getTime() - 86_400_000)
    const yesterdayStr = toDateKeyTz(yesterdayDate, tz)
    const prevStart = zonedTimeToUtc(`${yesterdayStr}T00:00:00`, tz)
    return { start, end, prevStart }
  }

  if (period === "this_month") {
    const parts = getTimeZoneFormattedParts(now, tz)
    const monthStartStr = `${todayStr.slice(0, 8)}01`
    const start = zonedTimeToUtc(`${monthStartStr}T00:00:00`, tz)

    const prevMonth = parts.month === 1 ? 12 : parts.month - 1
    const prevYear = parts.month === 1 ? parts.year - 1 : parts.year
    const prevMonthStartStr = `${prevYear}-${String(prevMonth).padStart(2, "0")}-01`
    const prevStart = zonedTimeToUtc(`${prevMonthStartStr}T00:00:00`, tz)

    // Clamp the day to the previous month's length, so 31 March compares against 28 February.
    const daysInPrevMonth = new Date(prevYear, prevMonth, 0).getDate()
    const prevDay = Math.min(parts.day, daysInPrevMonth)
    const prevEndStr = `${prevYear}-${String(prevMonth).padStart(2, "0")}-${String(prevDay).padStart(2, "0")}`
    const prevEnd = zonedTimeToUtc(`${prevEndStr}T00:00:00`, tz)

    // Carry the time of day across, so a comparison made at noon compares half-days.
    const todayMidnight = zonedTimeToUtc(`${todayStr}T00:00:00`, tz)
    const timeOfDayMs = end.getTime() - todayMidnight.getTime()
    return { start, end, prevStart, prevEnd: new Date(prevEnd.getTime() + timeOfDayMs) }
  }

  if (period === "custom") {
    const { from, to } = options.customRange ?? {}
    // Loud rather than silent: without both bounds the preset branch below would look up a
    // day count that does not exist and hand back an Invalid Date, which reaches SQL as NaN.
    if (!from || !to) {
      throw new Error("A custom period requires both `from` and `to` (YYYY-MM-DD)")
    }
    const start = zonedTimeToUtc(`${from}T00:00:00`, tz)
    const toEnd = zonedTimeToUtc(`${to}T23:59:59`, tz)
    const lengthMs = toEnd.getTime() - start.getTime()
    return { start, end: toEnd, prevStart: new Date(start.getTime() - lengthMs) }
  }

  if (period === "all") {
    const guardrailStart = new Date(end.getTime() - MAX_HISTORY_YEARS * 365 * 86_400_000)
    const { oldestDate } = options
    const start = oldestDate && oldestDate > guardrailStart ? oldestDate : guardrailStart
    // No earlier window exists to compare against.
    return { start, end, prevStart: start }
  }

  const days = PRESET_DAYS[period]
  const startDate = new Date(now.getTime() - days * 86_400_000)
  const startStr = toDateKeyTz(startDate, tz)
  const start = zonedTimeToUtc(`${startStr}T00:00:00`, tz)
  return { start, end, prevStart: new Date(start.getTime() - days * 86_400_000) }
}
